import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Preloader from '../components/Preloader';
import Footer from '../components/Footer';
import { FaArrowLeft } from 'react-icons/fa';
import ScrollReveal from '../components/ScrollReveal';

const festivalsData = [
    {
        id: 1,
        title: 'Homowo Festival',
        region: 'Greater Accra Region',
        description: 'A harvest festival celebrated by the Ga people with parades and street celebrations.',
        image: '/assets/homowo.jpg',
        fullContent: 'Homowo means "to hoot at hunger" in the Ga language. This vibrant festival celebrates the successful farming season with music, dance, and traditional ceremonies. The festival is marked by colorful parades, traditional food preparation, and community gatherings that strengthen social bonds.',
        month: 'August-September',
        images: ['/assets/homowo3.jpg', '/assets/homowo4.webp', '/assets/homowo6.webp'],
        traditions: ['Harvest Celebration', 'Parades', 'Traditional Meals', 'Community Bonding'],
    },
    {
        id: 2,
        title: 'Cape Town Minstrel Carnival',
        region: 'Western Cape',
        description: 'A vibrant and colourful parade celebrating the "Second New Year" with music, dance, and extravagant costumes.',
        image: '/assets/westerncapefestival.jpg',
        fullContent: 'The Cape Town Minstrel Carnival, also known as Kaapse Klopse, is a historic celebration held on the 2nd of January (Tweede Nuwe Jaar). Thousands of minstrels in brightly coloured costumes parade through the city, playing music and dancing, a tradition rooted in the history of slaves in the Cape being given a day off to celebrate.',
        month: 'January',
        images: ['/assets/minstrel1.jpg', '/assets/minstrel2.jpg', '/assets/minstrel3.webp'],
        traditions: ['Street Parades', 'Live Music', 'Colorful Costumes', 'Ghoema Drums'],
    },
    {
        id: 3,
        title: 'Timkat Festival',
        region: 'Addis Ababa',
        description: 'An exodus festival celebrated with colorful processions and water blessing ceremonies.',
        image: '/assets/timakat.jpg',
        fullContent: 'Timkat is one of Ethiopia\'s most important festivals, celebrating Epiphany with colorful processions, traditional music, and water blessing ceremonies.',
        month: 'January',
        images: ['/assets/timkat3.webp', '/assets/timkat2.jpg', '/assets/TimkatL05.jpg'],
        traditions: ['Royal Processions', 'Ancestral Ceremonies', 'Cultural Displays', 'Traditional Music'],
    },
];

const FestivalDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const festival = festivalsData.find(f => f.id === parseInt(id));

    if (!festival) {
        return (
            <div className='font-nixie-one bg-gray-50 min-h-screen text-gray-800'>
                <PageHeader />
                <Preloader />
                <main className="flex items-center justify-center min-h-screen">
                    <div className="text-center">
                        <h2 className="text-4xl font-bold mb-4">Festival Not Found</h2>
                        <button onClick={() => navigate('/cultural-festivals')} className="px-6 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-300">
                            Back to Festivals
                        </button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className='font-nixie-one bg-gray-50 min-h-screen text-gray-800'>
            <PageHeader />
            <Preloader />
            <main>
                {/* Back Button */}
                <section className="bg-white py-6 px-4 border-b">
                    <div className="container mx-auto">
                        <button onClick={() => navigate('/cultural-festivals')} className="flex items-center gap-2 text-green-600 hover:text-green-700 font-semibold transition-colors duration-300">
                            <FaArrowLeft /> Back to Festivals
                        </button>
                    </div>
                </section>

                {/* Hero Section */}
                <section className="relative h-96 bg-cover bg-center">
                    <img src={festival.image} alt={festival.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40"></div>
                    <div className="absolute inset-0 flex items-end justify-start">
                        <div className="p-8 text-white">
                            <span className="inline-block bg-green-600 text-white text-xs font-bold px-3 py-1 rounded mb-4">{festival.month}</span>
                            <h1 className="text-4xl md:text-6xl font-cormorant-upright font-bold">{festival.title}</h1>
                            <p className="text-gray-200 mt-2">{festival.region}</p>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section className="py-16 px-4">
                    <div className="container mx-auto max-w-4xl">
                        <ScrollReveal animationClass="animate-fade-in" threshold={0.1}>
                            <article className="bg-white rounded-lg shadow-lg p-8">
                                <h2 className="text-2xl font-cormorant-upright font-bold mb-6">About This Festival</h2>
                                <p className="text-black font-semibold text-sm leading-relaxed mb-8">{festival.fullContent}</p>

                                {/* Photo Gallery */}
                                <div className="mb-8">
                                    <h3 className="text-2xl font-cormorant-upright font-bold mb-6">Festival Gallery</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {festival.images.map((image, index) => (
                                            <div key={index} className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
                                                <img
                                                    src={image}
                                                    alt={`${festival.title} ${index + 1}`}
                                                    className="w-full h-48 object-cover transform hover:scale-105 transition-transform duration-500"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Festival Traditions */}
                                <div className="border-t pt-8">
                                    <h3 className="text-2xl font-cormorant-upright font-bold mb-6">Key Traditions</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {festival.traditions.map((tradition, index) => (
                                            <div key={index} className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
                                                <p className="text-gray-700 font-semibold text-sm">{tradition}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Festival Info Box */}
                                <div className="mt-8 bg-green-50 border-l-4 border-green-600 p-6 rounded">
                                    <h4 className="font-bold text-lg mb-2">Festival Information</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                                        <div>
                                            <p className="font-semibold text-green-600 text-sm">When</p>
                                            <p className='text-sm font-semibold'>{festival.month}</p>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-green-600 text-sm">Where</p>
                                            <p className='text-sm font-semibold'>{festival.region}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Call to Action */}
                                <div className="mt-8 bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded">
                                    <h4 className="font-bold text-base mb-2">Plan Your Visit</h4>
                                    <p className="mb-4 text-sm">Experience the vibrant celebrations of this cultural festival and create unforgettable memories.</p>
                                    <a href='/contact-us'>
                                        <button className="px-6 py-2 bg-white text-sm text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-300">
                                            Get Started
                                        </button>
                                    </a>
                                </div>
                            </article>
                        </ScrollReveal>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default FestivalDetail;
